import React from 'react';
import { Card, Pill } from '../MetricsUI';
import { RANGES, isoDay, windowBody } from '../cohort/CohortFilters';

/*
 * The admin console's filter bar.
 *
 * It used to reuse the manager page's bar with `filterOptions={{}}`, which left
 * pickers for case study, chapter, avatar and learning level sitting above a
 * page where none of them changes a single number — the console reports on
 * ORGANISATIONS, not on content or on one roster. They were placeholders.
 *
 * What is here instead is what actually narrows this page: the date window,
 * which is the only filter the server sees; the region, now that an
 * organisation can exist in more than one; and a name search with a minimum
 * size, because a platform of 45 organisations includes a long tail of
 * one-person test accounts that drag every median around.
 */

export const EMPTY_ADMIN_FILTERS = {
  range: '30d',
  // Only used when range === 'custom'. YYYY-MM-DD, in the viewer's timezone.
  from: '',
  to: '',
  /** Canonical region ids, empty meaning every region. */
  regions: [],
  query: '',
  /** Hide organisations with fewer than this many members. 0 shows all. */
  minMembers: 0,
};

const REGION_LABEL = { us: 'US', eu: 'EU', ap: 'APAC' };

const SIZES = [
  { key: 0, label: 'All sizes' },
  { key: 2, label: '2+ members' },
  { key: 5, label: '5+ members' },
  { key: 10, label: '10+ members' },
];

/**
 * Apply the row-level filters.
 *
 * The date window is NOT applied here — it changes what the server fetches, so
 * it is already reflected in the rows. Region, name and size narrow what is
 * already in hand, which is why they respond instantly.
 */
export const applyAdminFilters = (organizations, filters) => {
  const q = (filters.query || '').trim().toLowerCase();
  return (organizations || []).filter((o) => {
    if (q && !String(o.name || '').toLowerCase().includes(q)) return false;
    if (filters.minMembers && (o.members || 0) < filters.minMembers) return false;
    if (filters.regions && filters.regions.length) {
      const inRegion = (o.regions || []).some((r) => filters.regions.includes(r));
      if (!inRegion) return false;
    }
    return true;
  });
};

export default function AdminFilters({
  filters,
  onChange,
  organizations = [],
  regions = [],
  /**
   * Members and organisations per region, as the whole platform, regardless of
   * what is selected.
   *
   * Counting the rows in hand instead made every unselected region read 0 the
   * moment one was chosen — which says "nothing there" when it means "not
   * showing it".
   */
  regionTotals = [],
}) {
  const set = (patch) => onChange({ ...filters, ...patch });

  const today = isoDay(new Date());
  const resolved = windowBody(filters);
  const customInvalid = filters.range === 'custom' && !resolved;
  const fmt = (ms) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const rangeLabel = resolved
    ? resolved.sinceStart
      ? 'Everything on record'
      : `${fmt(resolved.from)} – ${fmt(resolved.to)}`
    : 'No range selected';

  /* Region columns come from what the server reported, so an environment with
     one region does not get two empty buttons. */
  const totalsBy = new Map(regionTotals.map((t) => [t.region, t]));
  const regionKeys = ['us', 'eu', 'ap'].filter(
    (r) => totalsBy.has(r) || regions.some((x) => x.region === r)
  );
  const regionCount = (r) => (totalsBy.get(r) ? totalsBy.get(r).organizations : 0);
  const failed = regions.filter((r) => !r.ok);

  const toggleRegion = (r) =>
    set({
      regions: filters.regions.includes(r)
        ? filters.regions.filter((x) => x !== r)
        : [...filters.regions, r],
    });

  const dirty =
    filters.regions.length > 0 || !!filters.query || filters.minMembers > 0 || filters.range !== '30d';

  return (
    <Card className="space-y-4 p-5">
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Date range</p>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <Pill
              key={r.key}
              active={filters.range === r.key}
              onClick={() =>
                set(
                  // Seed a sensible custom range rather than opening on two
                  // empty fields and no data.
                  r.custom && !filters.from
                    ? { range: r.key, from: isoDay(new Date(Date.now() - 30 * 86400000)), to: isoDay(new Date()) }
                    : { range: r.key }
                )
              }
            >
              {r.label}
            </Pill>
          ))}
        </div>

        {filters.range === 'custom' && (
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-xs text-gray-500">
              <span className="mb-1 block font-semibold uppercase tracking-wide text-gray-400">From</span>
              <input
                type="date"
                value={filters.from}
                max={filters.to || today}
                onChange={(e) => set({ from: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-lavender focus:outline-none"
              />
            </label>
            <label className="text-xs text-gray-500">
              <span className="mb-1 block font-semibold uppercase tracking-wide text-gray-400">To</span>
              <input
                type="date"
                value={filters.to}
                min={filters.from}
                max={today}
                onChange={(e) => set({ to: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-lavender focus:outline-none"
              />
            </label>
          </div>
        )}

        <p className={`mt-2 text-xs ${customInvalid ? 'text-red-600' : 'text-gray-400'}`}>
          {customInvalid ? 'Pick a start date before the end date.' : rangeLabel}
        </p>
      </div>

      {regionKeys.length > 1 && (
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Region</p>
          <div className="flex flex-wrap items-center gap-2">
            {/*
              * "All regions" is a pill like the others, active when nothing
              * specific is chosen.
              *
              * It was a text link that appeared only once a region was
              * selected and vanished again on click — a control that is absent
              * until you need it is a control nobody finds, and one that
              * disappears when used looks broken. As a pill it is always there
              * and always shows which state you are in.
              */}
            <Pill active={filters.regions.length === 0} onClick={() => set({ regions: [] })}>
              All regions
            </Pill>
            {regionKeys.map((r) => (
              <Pill key={r} active={filters.regions.includes(r)} onClick={() => toggleRegion(r)}>
                {REGION_LABEL[r] || r} · {regionCount(r)}
              </Pill>
            ))}
          </div>
          {filters.regions.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Every figure on this page covers{' '}
              {filters.regions.map((r) => REGION_LABEL[r] || r).join(' and ')} only — members, adoption and
              health are that region&rsquo;s, not the whole organisation&rsquo;s.
            </p>
          )}
          {/*
            * A region that did not answer is not a region with no organisations.
            *
            * The REASON is shown, not just the fact. dbmaster distinguishes a region
            * with no address in its .env from one that was asked and failed, and
            * those have completely different fixes — but this banner printed "did
            * not answer" for both and threw the explanation away, which is how a
            * one-line configuration gap turned into a long hunt once already.
            */}
          {failed.length > 0 && (
            <div className="mt-2 text-xs text-amber-700">
              <p>
                Organisations in{' '}
                {failed.map((r) => REGION_LABEL[r.region] || r.region).join(', ')} are missing from every
                figure on this page.
              </p>
              <ul className="mt-1 space-y-0.5">
                {failed.map((r) => (
                  <li key={r.region}>
                    <b>{REGION_LABEL[r.region] || r.region}:</b>{' '}
                    <span className="font-normal text-amber-800">{r.error || 'did not answer'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-gray-500">
          <span className="mb-1 block font-semibold uppercase tracking-wide text-gray-400">Organisation</span>
          <input
            value={filters.query}
            onChange={(e) => set({ query: e.target.value })}
            placeholder="Search by name"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-lavender focus:outline-none"
          />
        </label>
        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Minimum size</p>
          <div className="flex flex-wrap gap-2">
            {SIZES.map((s) => (
              <Pill key={s.key} active={filters.minMembers === s.key} onClick={() => set({ minMembers: s.key })}>
                {s.label}
              </Pill>
            ))}
          </div>
        </div>
        {dirty && (
          <button
            onClick={() => onChange(EMPTY_ADMIN_FILTERS)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:shadow"
          >
            Reset
          </button>
        )}
      </div>
    </Card>
  );
}
